import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './BudgetBreakdown.module.css';
import BottomNavBar from '../components/BottomNavBar';
import InstructionCard from '../components/InstructionCard';
import LivesDisplay from '../components/LivesDisplay';
import { useAuth } from '../context/AuthContext';
import plaidService from '../services/plaidService';
import { deleteAccount } from '../services/authService';

// Rank progression data
const RANKS = [
  { level: 1, name: 'Budget Rookie', xp: 0 },
  { level: 2, name: 'Savings Scout', xp: 100 },
  { level: 3, name: 'Money Manager', xp: 300 },
  { level: 4, name: 'Finance Journeyman', xp: 500 },
  { level: 5, name: 'Dollar Defender', xp: 1000 },
  { level: 6, name: 'Wealth Warrior', xp: 1500 },
  { level: 7, name: 'Budget Baron', xp: 2000 },
  { level: 8, name: 'Finance Master', xp: 3000 },
  { level: 9, name: 'Money Mogul', xp: 4000 },
  { level: 10, name: 'Financial Legend', xp: 5000 },
];

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [xp, setXp] = useState<number>(0);
  const [isDisconnecting, setIsDisconnecting] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [hasBankConnected, setHasBankConnected] = useState<boolean>(false);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState<boolean>(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState<boolean>(false);
  const { isAuthenticated, logout } = useAuth();

  useEffect(() => {
    const savedXp = localStorage.getItem('user_xp');
    if (savedXp && !isNaN(Number(savedXp))) {
      setXp(Number(savedXp));
    }
    
    // Check if bank account is connected
    checkBankConnection();
  }, []);
  
  const checkBankConnection = async () => {
    try {
      const accounts = await plaidService.fetchAccounts();
      setHasBankConnected(accounts.length > 0);
    } catch (error) {
      console.error('Error checking bank connection:', error);
      setHasBankConnected(false);
    }
  };

  // Determine current level from XP
  const currentRank = RANKS.slice().reverse().find(r => xp >= r.xp) || RANKS[0];
  const nextRank = RANKS.find(r => r.level === currentRank.level + 1);

  const handleDisconnectClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmDisconnect = async () => {
    try {
      setIsDisconnecting(true);
      await plaidService.disconnectAccount();
      setShowConfirmDialog(false);
      
      // Show success message
      alert('Bank account disconnected successfully. Your transaction data has been removed.');
      
      // Optionally refresh the page or redirect
      window.location.reload();
    } catch (error: any) {
      console.error('Error disconnecting account:', error);
      alert(error.message || 'Failed to disconnect bank account. Please try again.');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleCancelDisconnect = () => {
    setShowConfirmDialog(false);
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeletingAccount(true);
      await deleteAccount();
      
      // Clear all local storage
      localStorage.clear();
      
      // Show success message and logout
      alert('Your account has been successfully deleted.');
      logout();
      navigate('/login');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      alert(error.message || 'Failed to delete account. Please try again.');
    } finally {
      setIsDeletingAccount(false);
      setShowDeleteAccountDialog(false);
    }
  };

  const handleCancelDeleteAccount = () => {
    setShowDeleteAccountDialog(false);
  };

  return (
    <div className={styles.container} style={{
      paddingBottom: 200,
      overflowY: 'auto',
      height: '100vh',
      WebkitOverflowScrolling: 'touch'
    }}>
      <BottomNavBar />
      <h1 style={{ 
        textAlign: 'center', 
        color: 'hsl(260, 80%, 74%)', 
        fontSize: '2.5rem', 
        fontWeight: 700, 
        marginTop: '75px', 
        marginBottom: '1rem' 
      }}>Profile</h1>
      {/* Logout button */}
      {isAuthenticated && (
        <div style={{ marginTop: '0', marginBottom: '1rem', textAlign: 'center' }}>
          <button 
            onClick={logout} 
            style={{ 
              display: 'inline-block',
              margin: '0 auto',
              padding: '10px 24px',
              fontSize: '1rem',
              fontWeight: 600,
              color: '#fff',
              backgroundColor: '#dc2626',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
          >
            Logout
          </button>
        </div>
      )}
      <LivesDisplay />
      <InstructionCard
        title="Welcome to WealthPlay!"
        content={[
          "Earn XP and game lives, level up by completing quests, tracking your spending, and building healthy financial habits.",
          "Quests help you learn. Games make finance fun. Achievements celebrate your progress!",
          "Start by connecting your bank account and setting your income to unlock your first achievement."
        ]}
        emoji="👋"
        storageKey="profile_instructions_dismissed"
      />
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{
          background: '#ede9fe',
          color: '#7c3aed',
          fontWeight: 700,
          fontSize: '1.15rem',
          borderRadius: 12,
          padding: '24px 0 24px 0',
          margin: '0 auto 0 auto',
          textAlign: 'center',
          letterSpacing: 0.5,
          boxShadow: '0 2px 8px #a5b4fc22',
          border: '1.5px solid #a5b4fc',
          maxWidth: 420,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12
        }}>
          <div style={{ color: '#6b7280', fontWeight: 600, fontSize: '1.08rem', margin: 0, marginBottom: 2 }}>Level: {currentRank.level}</div>
          <div style={{ fontSize: '1.35rem', fontWeight: 700, marginBottom: 2, color: '#7c3aed' }}>{currentRank.name}</div>
          {/* XP, Next Unlock, Next Rank, Progress Bar */}
          <div style={{ width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
              <span style={{ fontWeight: 600, fontSize: '1.15rem', color: '#18181b' }}>XP: {xp}/{nextRank ? nextRank.xp : currentRank.xp + 1000}</span>
              {nextRank && (
                <span style={{
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  color: '#7c3aed',
                  marginLeft: 12,
                  textAlign: 'right',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  lineHeight: 1.15
                }}>
                  <span>Next Unlock:</span>
                  <span>{nextRank.name}!</span>
                </span>
              )}
            </div>
            <div style={{ width: '100%', height: 22, background: '#e9e1ff', borderRadius: 16, overflow: 'hidden', display: 'flex', marginTop: 8, position: 'relative' }}>
              <div style={{
                height: '100%',
                background: '#181e2a',
                borderTopLeftRadius: 16,
                borderBottomLeftRadius: 16,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                width: `${Math.min(100, Math.round(((xp - currentRank.xp) / ((nextRank ? nextRank.xp : currentRank.xp + 1000) - currentRank.xp)) * 100))}%`,
                transition: 'width 0.4s cubic-bezier(.4,1,.4,1)'
              }} />
              <div style={{
                height: '100%',
                background: '#fff',
                borderTopRightRadius: 16,
                borderBottomRightRadius: 16,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                width: `${100 - Math.min(100, Math.round(((xp - currentRank.xp) / ((nextRank ? nextRank.xp : currentRank.xp + 1000) - currentRank.xp)) * 100))}%`,
                transition: 'width 0.4s cubic-bezier(.4,1,.4,1)'
              }} />
            </div>
          </div>
        </div>
      </div> {/* End XP Bar */}
      {/* Achievements Section */}
      <div className={styles.achievementsSection} style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', top: 16, right: 24, zIndex: 2 }}>
          <span className={styles.unlockedBadge} style={{ marginBottom: 0 }}>
            {(localStorage.getItem('budget_income') ? 1 : 0) + (localStorage.getItem('streak_starter_unlocked') ? 1 : 0) + (localStorage.getItem('account_master_unlocked') ? 1 : 0) + (localStorage.getItem('budget_pro_unlocked') ? 1 : 0)}/4
          </span>
        </div>
        <h2 className={styles.achievementsTitle}>Achievements</h2>
        <div className={styles.achievementsRow}>
          {/* Enter Annual Income Achievement */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div
              className={
                styles.achievementIcon + ' ' + (localStorage.getItem('budget_income') ? styles.purple : styles.gray)
              }
            >
              📊
              <div className={styles.achievementTooltip}>
                <div style={{ fontWeight: 700, fontSize: '1.15rem' }}>Budget Master</div>
                <div style={{ fontWeight: 400, fontSize: '1rem', color: '#6b7280' }}>Create your first budget</div>
              </div>
            </div>
            <span style={{ fontWeight: 600, color: localStorage.getItem('budget_income') ? '#059669' : '#a1a1aa', fontSize: '1rem' }}>
              {localStorage.getItem('budget_income') ? 'Unlocked' : 'Locked'}
            </span>
          </div>
          {/* Streak Starter Achievement */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div
              className={
                styles.achievementIcon + ' ' + (localStorage.getItem('streak_starter_unlocked') ? styles.purple : styles.gray)
              }
            >
              🔥
              <div className={styles.achievementTooltip}>
                <div style={{ fontWeight: 700, fontSize: '1.15rem' }}>Streak Starter</div>
                <div style={{ fontWeight: 400, fontSize: '1rem', color: '#6b7280' }}>Log in 3 days in a row</div>
              </div>
            </div>
            <span style={{ fontWeight: 600, color: localStorage.getItem('streak_starter_unlocked') ? '#059669' : '#a1a1aa', fontSize: '1rem' }}>
              {localStorage.getItem('streak_starter_unlocked') ? 'Unlocked' : 'Locked'}
            </span>
          </div>
          {/* Account Master Achievement */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div
              className={
                styles.achievementIcon + ' ' + (localStorage.getItem('account_master_unlocked') ? styles.purple : styles.gray)
              }
            >
              🏦
              <div className={styles.achievementTooltip}>
                <div style={{ fontWeight: 700, fontSize: '1.15rem' }}>Account Master</div>
                <div style={{ fontWeight: 400, fontSize: '1rem', color: '#6b7280' }}>Connect your first account</div>
              </div>
            </div>
            <span style={{ fontWeight: 600, color: localStorage.getItem('account_master_unlocked') ? '#059669' : '#a1a1aa', fontSize: '1rem' }}>
              {localStorage.getItem('account_master_unlocked') ? 'Unlocked' : 'Locked'}
            </span>
          </div>
          {/* Budget Pro Achievement */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div
              className={
                styles.achievementIcon + ' ' + (localStorage.getItem('budget_pro_unlocked') ? styles.purple : styles.gray)
              }
            >
              🧮
              <div className={styles.achievementTooltip}>
                <div style={{ fontWeight: 700, fontSize: '1.15rem' }}>Budget Pro</div>
                <div style={{ fontWeight: 400, fontSize: '1rem', color: '#6b7280' }}>Use the app for 30 days</div>
              </div>
            </div>
            <span style={{ fontWeight: 600, color: localStorage.getItem('budget_pro_unlocked') ? '#059669' : '#a1a1aa', fontSize: '1rem' }}>
              {localStorage.getItem('budget_pro_unlocked') ? 'Unlocked' : 'Locked'}
            </span>
          </div>
        </div>
      </div>
      {/* End Achievements Section */}

      {/* Rank Progression Section */}
      <div style={{ margin: '48px auto 0 auto', maxWidth: 600 }}>
        <h2 style={{ fontWeight: 700, fontSize: '1.25rem', marginBottom: 24, textAlign: 'left' }}>Rank Progression</h2>
        <div>
          {RANKS.map(rank => {
            const isCurrent = rank.level === currentRank.level;
            const isCompleted = xp >= rank.xp && !isCurrent;
            return (
              <div
                key={rank.level}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 18,
                  background: isCurrent ? '#ede9fe' : 'transparent',
                  borderRadius: 12,
                  padding: isCurrent ? '18px 16px' : '12px 16px',
                  marginBottom: 10,
                  boxShadow: isCurrent ? '0 2px 12px #a78bfa22' : undefined,
                  border: isCurrent ? '2px solid #a78bfa' : '1.5px solid #e5e7eb',
                  position: 'relative',
                  transition: 'background 0.2s, border 0.2s',
                }}
              >
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: isCurrent || isCompleted ? '#a78bfa' : '#e5e7eb',
                  color: isCurrent || isCompleted ? '#fff' : '#a1a1aa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  border: isCurrent ? '2.5px solid #7c3aed' : 'none',
                  boxShadow: isCurrent ? '0 2px 8px #a78bfa33' : undefined,
                  marginRight: 8,
                  position: 'relative',
                }}>
                  {isCompleted ? <span style={{ fontSize: 22 }}>✔</span> : rank.level}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '1.13rem', color: isCurrent ? '#7c3aed' : '#18181b' }}>{rank.name}</div>
                  <div style={{ fontSize: '1rem', color: '#6b7280', fontWeight: 500 }}>Level {rank.level} • {rank.xp} XP required</div>
                </div>
                {isCurrent && (
                  <span style={{
                    background: '#a78bfa',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '1rem',
                    borderRadius: 8,
                    padding: '6px 18px',
                    marginLeft: 12,
                    boxShadow: '0 2px 8px #a78bfa33',
                  }}>
                    Current
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ 
              color: '#dc2626', 
              marginBottom: '16px',
              fontSize: '1.25rem',
              fontWeight: '600'
            }}>
              Disconnect Bank Account?
            </h3>
            <p style={{ 
              color: '#666', 
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              This will permanently remove your bank account connection and delete all transaction data. This action cannot be undone.
            </p>
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end' 
            }}>
              <button
                onClick={handleCancelDisconnect}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  backgroundColor: 'white',
                  color: '#374151',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDisconnect}
                disabled={isDisconnecting}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: isDisconnecting ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  opacity: isDisconnecting ? 0.7 : 1
                }}
              >
                {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Management Section */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '2rem',
        paddingTop: '1rem',
        borderTop: '1px solid #e5e7eb'
      }}>
        {/* Disconnect Bank Account */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            onClick={handleDisconnectClick}
            disabled={!hasBankConnected}
            style={{
              background: hasBankConnected ? '#dc2626' : '#d1d5db',
              color: hasBankConnected ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: hasBankConnected ? 'pointer' : 'not-allowed',
              transition: 'background 0.2s',
              marginBottom: '8px',
              opacity: hasBankConnected ? 1 : 0.6
            }}
            onMouseOver={(e) => hasBankConnected && (e.currentTarget.style.background = '#b91c1c')}
            onMouseOut={(e) => hasBankConnected && (e.currentTarget.style.background = '#dc2626')}
          >
            Disconnect Bank Account
          </button>
          <p style={{ 
            color: '#666', 
            fontSize: '0.8rem', 
            margin: '0 auto', 
            maxWidth: '300px',
            lineHeight: '1.4'
          }}>
            {hasBankConnected 
              ? 'Remove your bank account connection and delete all transaction data'
              : 'No bank account connected'}
          </p>
        </div>
        
        {/* Delete Account */}
        <div style={{ 
          paddingTop: '1rem',
          borderTop: '1px solid #e5e7eb'
        }}>
          <button
            onClick={() => setShowDeleteAccountDialog(true)}
            style={{
              background: '#991b1b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background 0.2s',
              marginBottom: '8px'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#7f1d1d'}
            onMouseOut={(e) => e.currentTarget.style.background = '#991b1b'}
          >
            Delete Account
          </button>
          <p style={{ 
            color: '#666', 
            fontSize: '0.8rem', 
            margin: '0 auto', 
            maxWidth: '300px',
            lineHeight: '1.4'
          }}>
            Permanently delete your account and all associated data
          </p>
        </div>
      </div>
      
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '2rem',
        paddingTop: '1rem',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'center',
        gap: '1rem',
        fontSize: '0.9rem'
      }}>
        <Link 
          to="/terms-of-service" 
          style={{ 
            color: '#666',
            textDecoration: 'none'
          }}
          onClick={(e) => {
            e.preventDefault();
            navigate('/terms-of-service');
          }}
        >
          Terms of Service
        </Link>
        <span style={{ color: '#999' }}>•</span>
        <Link 
          to="/privacy-policy" 
          style={{ 
            color: '#666',
            textDecoration: 'none'
          }}
          onClick={(e) => {
            e.preventDefault();
            navigate('/privacy-policy');
          }}
        >
          Privacy Policy
        </Link>
      </div>

      {/* Delete Account Confirmation Dialog */}
      {showDeleteAccountDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ 
              color: '#dc2626', 
              marginBottom: '16px',
              fontSize: '1.25rem',
              fontWeight: '600'
            }}>
              Delete Account?
            </h3>
            <p style={{ 
              color: '#666', 
              marginBottom: '16px',
              lineHeight: '1.5'
            }}>
              This will permanently delete your account and all associated data including:
            </p>
            <ul style={{
              color: '#666',
              marginBottom: '20px',
              paddingLeft: '20px',
              lineHeight: '1.6'
            }}>
              <li>Your profile information</li>
              <li>All transaction history</li>
              <li>Bank account connections</li>
              <li>Game progress and achievements</li>
              <li>Quest completion history</li>
            </ul>
            <p style={{ 
              color: '#dc2626', 
              marginBottom: '24px',
              fontWeight: '600'
            }}>
              This action cannot be undone!
            </p>
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end' 
            }}>
              <button
                onClick={handleCancelDeleteAccount}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  backgroundColor: 'white',
                  color: '#374151',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: isDeletingAccount ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  opacity: isDeletingAccount ? 0.7 : 1
                }}
              >
                {isDeletingAccount ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;